import { hasQueryPrefix, removeQueryPrefix } from "@/utils/query-prefix";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";
import { getTokenCookie } from "./token-cookie";
import { baseApi } from "./constants";

type Method = "GET" | "POST" | "PATCH" | "DELETE";

/**
 * Api class for making api calls
 *
 * @param baseURL base url of api
 * @param token token for authorization
 * @author Azher Uddin <https://github.com/AzherUddin617>
 * @date 2023-02-27 23:44:40
 */
export class Fetcher {
  baseURL: string;
  token: string;
  useToken: boolean;
  timeout: NodeJS.Timeout | null = null;
  logs: any[] = [];

  constructor(service?: string, baseURL?: string, token?: string) {
    this.baseURL = baseURL || baseApi;
    this.baseURL = service ? this.baseURL + service : this.baseURL;
    this.token = token || "";

    this.useToken = true;
    this.timeout = null;
  }

  /**
   * fetch data from api
   *
   * @param method method of request
   * @param path path of api
   * @param data data to send
   * @param query query params
   * @param queryString query string
   * @param config config for axios
   * @returns response of api call
   * @throws error if api call fails
   * @example fetch({
   *  method: 'GET',
   *  path: 'auth/customer/login',
   *  data: {
   *    "username": phone,
   *   "password": password,
   * }
   * @author Azher Uddin <https://github.com/AzherUddin617>
   * @date 2023-02-27 23:45:20
   */
  async fetch<Type = any>({
    method,
    path,
    data,
    query,
    queryString,
    config = {},
  }: {
    method: Method;
    path: string;
    data?: any;
    query?: any;
    queryString?: string;
    config?: AxiosRequestConfig<any> & { cache?: RequestCache };
  }): Promise<AxiosResponse<Type, any>> {
    // prefer an explicitly provided token (e.g. admin preview) over the cookie
    const token = this.token || getTokenCookie();

    const url = `${this.baseURL}${path}${
      queryString ? +"?" + queryString : ""
    }`;

    // to make time log
    let start = 0;

    // console.log("fetching page...Token:", token, "URL:", url);
    

    if (process.env.NEXT_PUBLIC_API_TIMELOG === "1")
      start = new Date().getTime();

    // On the server, route public GETs through native fetch so they hit
    // Next.js' data cache (5-minute revalidate). POSTs and all client-side
    // calls keep using axios. Server-side calls never carry a user token
    // (js-cookie can't read request cookies on the server), so cached
    // responses are always public data.
    if (typeof window === "undefined" && method === "GET" && !token) {
      const qs = query
        ? (url.includes("?") ? "&" : "?") +
          new URLSearchParams(
            Object.entries(query).reduce(
              (acc, [k, v]) => {
                if (v !== undefined && v !== null) acc[k] = String(v);
                return acc;
              },
              {} as Record<string, string>,
            ),
          ).toString()
        : "";

      const res = await fetch(url + qs, {
        headers: (config.headers as Record<string, string>) || {},
        // per-request opt-out of the data cache (e.g. full catalog feeds
        // that exceed Next.js' 2MB cache limit)
        ...(config.cache
          ? { cache: config.cache }
          : {
              next: {
                revalidate:
                  Number(process.env.PAGE_DATA_REVALIDATE) || 300,
              },
            }),
      } as RequestInit);

      if (!res.ok) {
        const err: any = new Error(`Request failed: ${res.status} ${url}`);
        err.response = { status: res.status };
        throw err;
      }

      return {
        data: (await res.json()) as Type,
        status: res.status,
        statusText: res.statusText,
        headers: {},
        config: {} as any,
      } as AxiosResponse<Type, any>;
    }

    return new Promise(async (resolve, reject) => {
      try {
        const response = await axios({
          method: method,
          url: url,
          data,
          ...config,
          headers: {
            ...config.headers,
            ...(this.useToken && token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : {}),
          },
          params: query,
        });

        resolve(response);
      } catch (error) {
        reject(error);
      } finally {
        if (process.env.NEXT_PUBLIC_API_TIMELOG === "1") {
          const end = new Date().getTime();
          const timeTaken = end - start;

          if (timeTaken >= 3000) {
            const log = {
              url,
              method,
              timeTaken: `${timeTaken}ms`,
            };

            this.logs.push(log);

            if (this.timeout) {
              clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(() => {
              // lazy import: requests/page.ts itself imports Fetcher, so a
              // top-level import here creates a circular dependency that
              // Turbopack rejects (TDZ error at module evaluation)
              import("@/requests/page").then(({ postApiLog }) => {
                postApiLog(this.logs);
                this.logs = [];
              });
            }, 5000);
          }
        }
      }
    });
  }

  /**
   * get data from api
   *
   * @param path path of api
   * @param config config for axios
   * @returns response of api call
   * @throws error if api call fails
   *
   * @author Azher Uddin <https://github.com/AzherUddin617>
   * @date 2023-02-27 23:46:16
   */
  async get<Type>(
    path: string,
    config?: AxiosRequestConfig<any> & { cache?: RequestCache }
  ): Promise<Type> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.fetch<Type>({
          method: "GET",
          path,
          config,
        });

        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * post data to api
   *
   * @param path path of api
   * @param data data to send
   * @param config config for axios
   * @returns response of api call
   * @throws error if api call fails
   *
   * @author Azher Uddin <https://github.com/AzherUddin617>
   * @date 2023-02-27 23:46:33
   */
  async post<Type>(
    path: string,
    data: any,
    config?: AxiosRequestConfig<any>
  ): Promise<Type> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.fetch<Type>({
          method: "POST",
          path,
          data,
          config,
        });

        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * patch data to api
   *
   * @param path path of api
   * @param data data to send
   * @param config config for axios
   * @returns response of api call
   * @throws error if api call fails
   *
   * @author Azher Uddin <https://github.com/AzherUddin617>
   * @date 2023-02-27 23:46:52
   */
  async patch<Type>(
    path: string,
    data: any,
    config?: AxiosRequestConfig<any>
  ): Promise<Type> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.fetch<Type>({
          method: "PATCH",
          path,
          data,
          config,
        });

        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * delete data from api
   *
   * @param path path of api
   * @param config config for axios
   * @returns response of api call
   * @throws error if api call fails
   *
   * @author Azher Uddin <https://github.com/AzherUddin617>
   * @date 2023-02-27 23:47:08
   */
  async delete<Type>(
    path: string,
    config?: AxiosRequestConfig<any>
  ): Promise<Type> {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await this.fetch<Type>({
          method: "DELETE",
          path,
          config,
        });

        resolve(response.data);
      } catch (error) {
        reject(error);
      }
    });
  }

  // set if use token
  setUseToken(useToken: boolean) {
    this.useToken = useToken;
  }

  // build query string
  buildQuery(query: any) {
    let queryString = "";

    for (const key in query) {
      if (
        query[key] !== undefined &&
        query[key] !== null &&
        query[key] !== ""
      ) {
        queryString += `${key}=${query[key]}&`;
      }
    }

    return queryString.at(-1) === "&" ? queryString.slice(0, -1) : queryString;
  }

  // build query with prefix string
  buildQueryPrefix(query: any, withoutPrefix = false) {
    let queryString = "";

    for (const key in query) {
      if (
        query[key] !== undefined &&
        query[key] !== null &&
        query[key] !== "" &&
        (hasQueryPrefix(key) || withoutPrefix)
      ) {
        queryString += `${removeQueryPrefix(key)}=${query[key]}&`;
      }
    }

    return queryString.at(-1) === "&" ? queryString.slice(0, -1) : queryString;
  }

  v(v: string) {
    const baseUrl = this.baseURL?.replace("api/v1/", `api/v${v}/`);
    return new Fetcher(undefined, baseUrl, this.token);
  }
}
